import { IBaseComponent } from '@codesuma/baseline'

// Pull a slide past the top of its scroll and it emits 'pull-down'. It is how
// the calendar goes up a level: the month opens the year, the year opens the
// years view. What that means is the caller's to say.
//
// The band is a real scroller, so this reads its overscroll rather than taking
// the gesture away from it: while scrollTop is 0 there is nothing for the
// browser to scroll, and the slide travels instead. Nothing is prevented and no
// touch-action is overridden — scrolling up to the top and carrying on is one
// continuous movement.
//
// Movement comes from touchmove as well as pointermove because the browser
// fires pointercancel the moment it takes a touch for scrolling, while touch
// events keep coming. This is the one place in the app that needs both.

// How far the finger must travel before the pull counts, and how far the slide
// is allowed to follow it. The slide moves at a third of the finger's distance,
// so the threshold is reached well before the cap.
const THRESHOLD = 64
const MAX_TRAVEL = 90

// `when` can hold the pull off for a while: the month holds it off while a
// day's sheet is up over it.
export const pullDown = (slide: IBaseComponent<'div'>, scroller: HTMLElement, when = () => true) => {
    let startX = 0
    let startY = 0
    let pulled = 0
    let active = false

    const settle = (animate: boolean) => {
        slide.style({
            transition: animate ? 'transform 0.25s ease' : 'none',
            transform: 'translateY(0)',
        })
    }

    const begin = (x: number, y: number) => {
        // A mouse released off the band leaves the slide where it stopped; the
        // next touch is the moment to put it back.
        settle(false)
        // Only from a standstill at the very top; anywhere else this is a scroll.
        active = when() && scroller.scrollTop <= 0
        startX = x
        startY = y
        pulled = 0
    }

    const move = (x: number, y: number) => {
        if (!active) return
        // The band got moving after all (upward flick, or a bounce settling) —
        // hand the gesture back.
        if (scroller.scrollTop > 0) {
            active = false
            settle(true)
            return
        }
        const dy = y - startY
        // Sideways belongs to the page's own slider, which is already on it.
        if (dy <= 0 || Math.abs(x - startX) > dy) {
            pulled = 0
            settle(false)
            return
        }
        pulled = dy
        slide.style({
            transition: 'none',
            transform: `translateY(${Math.min(dy / 3, MAX_TRAVEL)}px)`,
        })
    }

    const end = () => {
        if (!active) return
        active = false
        settle(true)
        if (pulled > THRESHOLD) slide.emit('pull-down')
    }

    scroller.addEventListener('pointerdown', (e: PointerEvent) => begin(e.clientX, e.clientY))
    scroller.addEventListener('pointermove', (e: PointerEvent) => move(e.clientX, e.clientY))
    scroller.addEventListener('touchmove', (e: TouchEvent) => {
        const t = e.touches[0]
        if (t) move(t.clientX, t.clientY)
    }, { passive: true })
    // pointerup covers the mouse; touchend covers the touch the browser
    // cancelled the pointer for. Not pointercancel — on touch that is the
    // browser claiming the scroll, which is the very gesture being followed.
    scroller.addEventListener('pointerup', end)
    scroller.addEventListener('pointerleave', (e: PointerEvent) => {
        if (e.pointerType === 'mouse') end()
    })
    scroller.addEventListener('touchend', end)
    scroller.addEventListener('touchcancel', end)
}
